import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatIDR } from "@/lib/format";
import { toast } from "sonner";
import { usePager, Pager } from "@/components/kiyana/Pager";
import { Wallet, Plus, Pencil, Trash2, X, Check, Info } from "lucide-react";
import EmpPicker from "@/components/EmpPicker";

const inp = "h-9 px-3 rounded-lg bg-white border border-slate-200 text-xs text-slate-700 focus:outline-none focus:border-[#C4738A]";
const blank = { name: "", kind: "add", amount: "", scope: "all", role_ids: [], user_ids: [], active: true, note: "" };

export default function AdminRemuneration() {
  const [data, setData] = useState(null);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(blank);

  const load = useCallback(() => {
    setData(null);
    api.get("/admin/remuneration").then((r) => setData(r.data))
      .catch((e) => { setData(false); toast.error(e?.response?.data?.detail || "Gagal memuat remunisasi"); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const openModal = (item) => {
    setForm(item ? { name: item.name, kind: item.kind, amount: String(item.amount), scope: item.scope, role_ids: item.role_ids || [], user_ids: item.user_ids || [], active: item.active, note: item.note || "" } : blank);
    setModal({ item: item || null });
  };

  const save = async () => {
    const payload = { ...form, amount: parseInt(form.amount) || 0, name: form.name.trim(), note: form.note.trim() };
    try {
      if (modal.item) await api.put(`/admin/remuneration/${modal.item.id}`, payload);
      else await api.post("/admin/remuneration", payload);
      toast.success(modal.item ? "Komponen diperbarui" : "Komponen ditambahkan");
      setModal(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan komponen"); }
  };

  const toggle = async (c) => {
    try {
      const r = await api.put(`/admin/remuneration/${c.id}/toggle`);
      toast.success(`${c.name} ${r.data.active ? "diaktifkan" : "dinonaktifkan"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengubah status"); }
  };

  const del = async (c) => {
    if (!window.confirm(`Hapus komponen "${c.name}"? Payroll yang sudah dibuat tidak berubah.`)) return;
    try { await api.delete(`/admin/remuneration/${c.id}`); toast.success("Komponen dihapus"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus"); }
  };

  const scopeLabel = (c) => c.scope === "all" ? "Semua Karyawan"
    : c.scope === "roles" ? `Role: ${(c.role_names || []).join(", ") || "-"}`
    : `${(c.user_names || []).length} orang: ${(c.user_names || []).join(", ")}`;

  const items = data?.items || [];
  const { paged: pagedRem, pager: remPager } = usePager(items);
  return (
    <div data-testid="rem-page" className="space-y-5 pb-10">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Remunisasi Karyawan</h1>
          <p className="text-xs text-slate-400">Atur tunjangan & potongan tetap (BPJS, tunjangan jabatan, dll) yang otomatis masuk payroll.</p>
        </div>
        <button data-testid="rem-add" onClick={() => openModal(null)} className="h-9 px-4 rounded-lg bg-[#7C2D3E] hover:bg-[#66222f] text-white text-xs font-semibold flex items-center gap-1.5 transition-colors">
          <Plus className="w-3.5 h-3.5" /> Tambah Komponen
        </button>
      </div>

      <div className="bg-blue-50/60 border border-blue-100 rounded-xl px-4 py-3 flex items-start gap-2 text-[11px] text-blue-700">
        <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <span>Komponen <b>aktif</b> dengan nominal di atas 0 otomatis masuk saat <b>Buat Payroll</b> sesuai sasarannya. Isi nominal 0 atau geser tombol ke Nonaktif untuk mematikan tanpa menghapus. Payroll yang sudah dibuat tidak berubah retroaktif.</span>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="text-[10px] uppercase tracking-wide text-slate-400 bg-slate-50/60 border-b border-slate-100">
              <th className="text-left px-4 py-2.5 font-semibold">Komponen</th>
              <th className="text-center px-3 py-2.5 font-semibold">Jenis</th>
              <th className="text-right px-3 py-2.5 font-semibold">Nominal / Periode</th>
              <th className="text-left px-3 py-2.5 font-semibold">Sasaran</th>
              <th className="text-center px-3 py-2.5 font-semibold">Status</th>
              <th className="px-4 py-2.5 w-24"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {data === null && <tr><td colSpan={6} className="px-4 py-12 text-center text-xs text-slate-400">Memuat...</td></tr>}
            {pagedRem.map((c) => (
              <tr key={c.id} data-testid={`rem-row-${c.id}`} className={!c.active ? "opacity-55" : ""}>
                <td className="px-4 py-2.5">
                  <div className="text-xs font-semibold text-slate-800">{c.name}</div>
                  {c.note && <div className="text-[9px] text-slate-400">{c.note}</div>}
                </td>
                <td className="px-3 py-2.5 text-center">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${c.kind === "add" ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-500"}`}>
                    {c.kind === "add" ? "Tambahan" : "Potongan"}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-right text-xs font-bold text-slate-800">{formatIDR(c.amount)}</td>
                <td className="px-3 py-2.5 text-[11px] text-slate-500 max-w-[260px] truncate" title={scopeLabel(c)}>{scopeLabel(c)}</td>
                <td className="px-3 py-2.5 text-center">
                  <button data-testid={`rem-toggle-${c.id}`} onClick={() => toggle(c)} title={c.active ? "Nonaktifkan" : "Aktifkan"}
                    className={`relative inline-flex w-9 h-5 rounded-full transition-colors ${c.active ? "bg-emerald-400" : "bg-slate-200"}`}>
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${c.active ? "left-[18px]" : "left-0.5"}`} />
                  </button>
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex gap-1 justify-end">
                    <button data-testid={`rem-edit-${c.id}`} onClick={() => openModal(c)} title="Ubah" className="w-8 h-8 rounded-lg text-slate-400 hover:text-[#C4738A] hover:bg-rose-50 flex items-center justify-center transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
                    <button data-testid={`rem-del-${c.id}`} onClick={() => del(c)} title="Hapus" className="w-8 h-8 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 flex items-center justify-center transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {data && !items.length && (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-xs text-slate-400">
                <Wallet className="w-8 h-8 mx-auto mb-2 text-slate-200" />Belum ada komponen — klik Tambah Komponen.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      <Pager {...remPager} testid="rem-pager" />

      {modal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setModal(null)}>
          <div data-testid="rem-modal" className="bg-white rounded-2xl p-5 w-full max-w-md space-y-3 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h4 className="font-display font-bold text-sm text-slate-900">{modal.item ? "Ubah Komponen" : "Tambah Komponen"}</h4>
              <button data-testid="rem-close" onClick={() => setModal(null)} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 mb-1.5">Nama Komponen</label>
              <input data-testid="rem-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="mis. Tunjangan Jabatan / BPJS Kesehatan" className={`${inp} w-full`} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-semibold text-slate-500 mb-1.5">Jenis</label>
                <select data-testid="rem-kind" value={form.kind} onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value }))} className={`${inp} w-full`}>
                  <option value="add">Tambahan (menambah gaji)</option>
                  <option value="ded">Potongan (mengurangi gaji)</option>
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-500 mb-1.5">Nominal per Periode (Rp)</label>
                <input data-testid="rem-amount" type="number" min="0" step="1000" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} placeholder="0 = nonaktif" className={`${inp} w-full`} />
              </div>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 mb-1.5">Sasaran</label>
              <div className="flex gap-2 flex-wrap">
                {[["all", "Semua Karyawan"], ["roles", "Role Tertentu"], ["people", "Orang Tertentu"]].map(([v, l]) => (
                  <button key={v} data-testid={`rem-scope-${v}`} onClick={() => setForm((f) => ({ ...f, scope: v }))}
                    className={`h-8 px-3.5 rounded-full text-[11px] font-semibold border transition-colors ${form.scope === v ? "border-[#C4738A] text-[#C4738A] bg-rose-50/50" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>{l}</button>
                ))}
              </div>
            </div>
            {form.scope === "roles" && (
              <div className="grid grid-cols-2 gap-1.5 p-3 rounded-xl bg-slate-50 border border-slate-100 max-h-40 overflow-y-auto">
                {(data?.options?.roles || []).map((r) => (
                  <label key={r.id} className="flex items-center gap-2 text-[11px] text-slate-600 cursor-pointer">
                    <input type="checkbox" data-testid={`rem-role-${r.id}`} className="accent-[#C4738A]"
                      checked={form.role_ids.includes(r.id)}
                      onChange={(e) => setForm((f) => ({ ...f, role_ids: e.target.checked ? [...f.role_ids, r.id] : f.role_ids.filter((x) => x !== r.id) }))} />
                    {r.label}
                  </label>
                ))}
              </div>
            )}
            {form.scope === "people" && (
              <EmpPicker tid="rem-people" options={data?.options?.employees || []} value={form.user_ids}
                onChange={(ids) => setForm((f) => ({ ...f, user_ids: ids }))} placeholder="Pilih karyawan..." />
            )}
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 mb-1.5">Catatan (tampil di rincian payroll)</label>
              <input data-testid="rem-note" value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} placeholder="mis. Sesuai kebijakan 2026" className={`${inp} w-full`} />
            </div>
            <label className="flex items-center gap-2 text-[11px] text-slate-600 cursor-pointer">
              <input type="checkbox" data-testid="rem-active" className="accent-[#C4738A]" checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} />
              Aktif — langsung dipakai saat Buat Payroll berikutnya
            </label>
            <div className="flex gap-2 pt-1">
              <button data-testid="rem-cancel" onClick={() => setModal(null)} className="flex-1 h-9 rounded-lg border border-slate-200 text-slate-500 text-xs font-semibold hover:border-slate-300">Batal</button>
              <button data-testid="rem-save" onClick={save} className="flex-1 h-9 rounded-lg bg-[#7C2D3E] text-white text-xs font-semibold hover:bg-[#66222f] flex items-center justify-center gap-1.5"><Check className="w-3.5 h-3.5" /> Simpan</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
